// src/pages/BuyNumbers.jsx
import { useState, useEffect, useCallback, useRef } from "react";
import api from "../api/api";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  RefreshCw, Copy, ShieldCheck, Clock,
  Search, X, History, ChevronDown, ChevronUp,
  Wifi, MapPin, AlertCircle, AlertTriangle, Loader2, Zap, Globe,
} from "lucide-react";
import { useActivations } from "../context/ActivationsContext";
import Currency from "../components/Currency";

const RENTAL_DURATION_MINUTES_STANDARD    = 20; // Daisy (daisysms)
const RENTAL_DURATION_MINUTES_ALTERNATIVE = 20; // GetaText
const CANCEL_LOCK_SECONDS_STANDARD        = 15; // Daisy — matches backend
const CANCEL_LOCK_SECONDS_ALTERNATIVE     = 60; // GetaText — matches backend
const PREMIUM_MULTIPLIER                  = 1.35;
const POST_PURCHASE_GUARD_MS      = 8000;

// ── All countries we want visible in the "All countries" tab and the
// service dropdown. Any country not found inside the loaded catalog will
// render as a disabled "Coming soon" row — purely a display list, it
// never affects catalog data or purchasing logic.
const ALL_COUNTRIES = [
  "USA", "Canada", "UK", "Nigeria", "Ghana", "Kenya", "South Africa",
  "India", "Philippines", "Indonesia", "Vietnam", "Pakistan", "Bangladesh",
  "Brazil", "Mexico", "Argentina", "Colombia", "Chile", "Peru",
  "Germany", "France", "Italy", "Spain", "Portugal", "Netherlands",
  "Belgium", "Poland", "Romania", "Ukraine", "Russia", "Turkey",
  "Sweden", "Norway", "Denmark", "Finland", "Ireland", "Switzerland",
  "Austria", "Greece", "Hungary", "Czech Republic",
  "Egypt", "Morocco", "Saudi Arabia", "UAE", "Israel",
  "China", "Japan", "South Korea", "Thailand", "Malaysia", "Singapore",
  "Australia", "New Zealand",
];

let _optimisticCounter = 0;
const newOptimisticId = () => `optimistic-${++_optimisticCounter}`;

// ── processNumber ─────────────────────────────────────────────────────────────
function processNumber(n, now = Date.now()) {
  const rentalMinutes =
    n.provider === "daisysms"
      ? RENTAL_DURATION_MINUTES_STANDARD
      : RENTAL_DURATION_MINUTES_ALTERNATIVE;

  let expiryMs = n.expiresAt ? Date.parse(n.expiresAt) : 0;
  if (!expiryMs && typeof n.expiresAt === "string")
    expiryMs = Date.parse(
      n.expiresAt.includes("Z") ? n.expiresAt : `${n.expiresAt}Z`
    );
  if (!expiryMs)
    expiryMs =
      (n.createdAt ? Date.parse(n.createdAt) : now) +
      rentalMinutes * 60 * 1000;

  let graceMs = n.graceExpiry ? Date.parse(n.graceExpiry) : 0;
  if (!graceMs && typeof n.graceExpiry === "string")
    graceMs = Date.parse(
      n.graceExpiry.includes("Z") ? n.graceExpiry : `${n.graceExpiry}Z`
    );

  const countdownTarget =
    n.otpReceived && graceMs && graceMs > now ? graceMs : expiryMs;
  const diff    = countdownTarget - now;
  const minutes = Math.max(0, Math.floor(diff / 60000));
  const seconds = Math.max(0, Math.floor((diff % 60000) / 1000));
  const expired = diff <= 0;

  const createdMs  = n.createdAt ? Date.parse(n.createdAt) : now;
  const lockSeconds =
    n.provider === "daisysms"
      ? CANCEL_LOCK_SECONDS_STANDARD
      : CANCEL_LOCK_SECONDS_ALTERNATIVE;
  const cancelLockLeft = Math.max(
    0,
    Math.ceil(lockSeconds - (now - createdMs) / 1000)
  );

  return {
    ...n,
    timeLeft:      { minutes, seconds, expired },
    cancelLockLeft,
    canCancel:
      n.status === "pending" && !n.otpReceived && cancelLockLeft === 0,
    isVisible:
      !expired || (n.otpReceived && graceMs && graceMs > now),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
export default function BuyNumbers() {
  const navigate = useNavigate();
  const token    = localStorage.getItem("accessToken");

  const {
    activations: rawActivations,
    activationsLoading,
    refreshActivations,
  } = useActivations();

  // ── API tab: "standard" (getatext) | "alternative" (textverified) | "allcountries"
  const [activeApi, setActiveApi] = useState("standard");
  const [animKey,   setAnimKey]   = useState(0);

  const dropdownRef     = useRef(null);
  const lastPurchaseRef = useRef(0);

  // ── Catalogs
  const [gtaCatalog,     setGtaCatalog]     = useState([]);
  const [tvCatalog,      setTvCatalog]      = useState([]);
  const [loadingCatalog, setLoadingCatalog] = useState(true);

  // ── Service selection
  const [selectedService, setSelectedService] = useState(null);
  const [serviceSearch,   setServiceSearch]   = useState("");
  const [showDropdown,    setShowDropdown]     = useState(false);

  // ── Filters (Standard only)
  const [carriers,        setCarriers]        = useState([]);
  const [loadingCarriers, setLoadingCarriers] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState("any");
  const [areaCodesInput,  setAreaCodesInput]  = useState("");
  const [filterError,     setFilterError]     = useState(null);
  const [showFilters,     setShowFilters]     = useState(false);

  // ── Active numbers
  const [activeNumbers, setActiveNumbers] = useState([]);
  const [loadingActive, setLoadingActive] = useState(true);
  const [buying,        setBuying]        = useState(false);
  const [cancelModal,   setCancelModal]   = useState(null);

  // ── History
  const [history,        setHistory]        = useState([]);
  const [showHistory,    setShowHistory]    = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // ── Switch hint
  const [showSwitchHint, setShowSwitchHint] = useState(false);

  useEffect(() => {
    if (!token) navigate("/login");
  }, [token, navigate]);

  // Close dropdown on outside click
  useEffect(() => {
    const h = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target))
        setShowDropdown(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    setFilterError(null);
  }, [selectedCarrier, areaCodesInput]);

  // ── Switch API tab
  const handleSwitchApi = (newApi) => {
    if (newApi === activeApi) return;
    setActiveApi(newApi);
    setAnimKey((k) => k + 1);
    setSelectedService(null);
    setServiceSearch("");
    setShowDropdown(false);
    setSelectedCarrier("any");
    setAreaCodesInput("");
    setFilterError(null);
    setCarriers([]);
    // Show the carrier/area-code panel immediately on Alternative —
    // no need to pick a service first.
    setShowFilters(newApi === "alternative");
    setShowSwitchHint(false);
  };

  const currentCatalog = activeApi === "standard" ? gtaCatalog : tvCatalog;

  // ── Popular service quick-chips — only rendered when the service actually
  // exists in the currently loaded catalog. No fake buttons, no guessing.
  const POPULAR_SERVICE_DEFS = [
    { key: "whatsapp", label: "WhatsApp", match: ["whatsapp"], color: "#25D366" },
    { key: "telegram", label: "Telegram", match: ["telegram"], color: "#26A5E4" },
    { key: "google",   label: "Google",   match: ["google"],   color: "#4285F4" },
    { key: "facebook", label: "Facebook", match: ["facebook"], color: "#1877F2" },
  ];

  const popularServices = POPULAR_SERVICE_DEFS
    .map((def) => ({
      ...def,
      svc: currentCatalog.find((s) =>
        def.match.some(
          (m) =>
            s.name.toLowerCase().includes(m) ||
            s.service.toLowerCase().includes(m)
        )
      ),
    }))
    .filter((p) => p.svc);

  const hasPremiumFilter =
    activeApi === "alternative" &&
    (selectedCarrier !== "any" || areaCodesInput.trim() !== "");

  const displayPrice = selectedService
    ? hasPremiumFilter
      ? Math.round(selectedService.priceNgn * PREMIUM_MULTIPLIER)
      : selectedService.priceNgn
    : null;

  const premiumExtra =
    selectedService && hasPremiumFilter
      ? displayPrice - selectedService.priceNgn
      : 0;

  // ── Fetch both catalogs on mount (one-time, not a poll — leave as-is)
  useEffect(() => {
    if (!token) return;
    const load = async () => {
      setLoadingCatalog(true);
      try {
        const [gtaRes, tvRes] = await Promise.allSettled([
          api.get("/usa/catalog"),
          api.get("/tv/catalog"),
        ]);
        if (gtaRes.status === "fulfilled" && gtaRes.value.data?.success) {
          setGtaCatalog(
            Object.values(gtaRes.value.data.catalog || {}).sort((a, b) =>
              a.name.localeCompare(b.name)
            )
          );
        }
        if (tvRes.status === "fulfilled" && tvRes.value.data?.success) {
          setTvCatalog(
            Object.values(tvRes.value.data.catalog || {}).sort((a, b) =>
              a.name.localeCompare(b.name)
            )
          );
        }
      } catch (err) {
        if (err.response?.status === 401) {
          localStorage.removeItem("accessToken");
          navigate("/login");
        } else {
          toast.error("Failed to load services. Please refresh.");
        }
      } finally {
        setLoadingCatalog(false);
      }
    };
    load();
  }, [token, navigate]);

  // ── Fetch carriers (Alternative only)
  const fetchCarriers = useCallback(async (serviceApiName) => {
    if (!serviceApiName) return;
    setLoadingCarriers(true);
    setCarriers([]);
    try {
      const res = await api.get(`/tv/service-details/${serviceApiName}`);
      if (res.data?.success && Array.isArray(res.data.carriers)) {
        setCarriers(res.data.carriers);
      }
    } catch {
      setCarriers([
        { value: "any",     label: "Any carrier", stock: null, available: true },
        { value: "at&t",    label: "AT&T",        stock: null, available: true },
        { value: "tmobile", label: "T-Mobile",    stock: null, available: true },
      ]);
    } finally {
      setLoadingCarriers(false);
    }
  }, []);

  const handleSelectService = (svc) => {
    setSelectedService(svc);
    setServiceSearch(svc.name);
    setShowDropdown(false);
    setSelectedCarrier("any");
    setAreaCodesInput("");
    setFilterError(null);
    setShowFilters(false);
    setShowSwitchHint(false);
    if (activeApi === "alternative") fetchCarriers(svc.service);
  };

  const handleClearService = () => {
    setServiceSearch("");
    setSelectedService(null);
    setShowDropdown(false);
    setSelectedCarrier("any");
    setAreaCodesInput("");
    setFilterError(null);
    setCarriers([]);
    setShowFilters(false);
    setShowSwitchHint(false);
  };

  // ── Fetch combined history (one-time on demand — leave as-is)
  const fetchHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const [gtaRes, tvRes] = await Promise.allSettled([
        api.get("/usa/history"),
        api.get("/tv/history"),
      ]);

      let combined = [];

      if (gtaRes.status === "fulfilled" && gtaRes.value.data?.success) {
        const gtaHistory = (gtaRes.value.data.history || [])
          .filter((h) => h.otpReceived === true && h.otp)
          .map((h) => ({ ...h, _source: "standard" }));
        combined = [...combined, ...gtaHistory];
      }
      if (tvRes.status === "fulfilled" && tvRes.value.data?.success) {
        const tvHistory = (tvRes.value.data.history || [])
          .filter((h) => h.otpReceived === true && h.otp)
          .map((h) => ({ ...h, _source: "alternative" }));
        combined = [...combined, ...tvHistory];
      }

      combined.sort(
        (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)
      );
      setHistory(combined);
    } catch {
      /* silent */
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  // ── Process the shared activations feed into gta/tv active-number cards.
  // This replaces the old fetchActiveNumbers(): it no longer calls
  // /user/activations/my itself — that now happens once, centrally, in
  // ActivationsContext. This function just reacts to that shared data.
  const processRawActivations = useCallback(
    (raw) => {
      // Skip reprocessing for a short window right after a purchase so the
      // optimistic card isn't yanked out before the real one is confirmed.
      if (Date.now() - lastPurchaseRef.current < POST_PURCHASE_GUARD_MS) {
        return;
      }

      const standardList = raw.filter(
        (n) =>
          n.provider === "daisysms" &&
          (n.country || "").toLowerCase().includes("usa") &&
          !["cancelled", "expired", "completed", "cancel_requested"].includes(
            n.status
          )
      );

      const altList = raw.filter(
        (n) =>
          n.provider === "getatext" &&
          (n.country || "").toLowerCase().includes("usa") &&
          !["cancelled", "expired", "completed", "cancel_requested"].includes(
            n.status
          )
      );

      const now       = Date.now();
      const processed = [...standardList, ...altList]
        .map((n) => processNumber(n, now))
        .filter((n) => n.isVisible);

      const realIds = new Set(processed.map((n) => n.activationId));

      setActiveNumbers((prev) => {
        const optimisticStillPending = prev.filter(
          (n) => n._optimistic && !realIds.has(n._realActivationId)
        );
        return [...optimisticStillPending, ...processed];
      });
      setLoadingActive(false);
    },
    []
  );

  // Reacts to the shared 5s poll from ActivationsContext instead of running
  // its own interval — this is the fix for the duplicate-polling pileup.
  useEffect(() => {
    if (activationsLoading) return;
    processRawActivations(rawActivations);
  }, [rawActivations, activationsLoading, processRawActivations]);

  // ── BUY NUMBER ─────────────────────────────────────────────────────────────
  const handleGetNumber = async () => {
    if (!selectedService || buying) return;
    setFilterError(null);
    setShowSwitchHint(false);
    setBuying(true);

    const optimisticId  = newOptimisticId();
    const now           = new Date();
    const rentalMinutes =
      activeApi === "standard"
        ? RENTAL_DURATION_MINUTES_STANDARD
        : RENTAL_DURATION_MINUTES_ALTERNATIVE;

    const optimisticEntry = {
      _optimistic:       true,
      _realActivationId: null,
      activationId:      optimisticId,
      phone:             null,
      service:           selectedService.service,
      status:            "pending",
      otpReceived:       false,
      provider:          activeApi === "standard" ? "daisysms" : "getatext",
      country:           "usa",
      chargedNgn:        displayPrice,
      priceNgn:          displayPrice,
      baseNgn:           selectedService.priceNgn,
      hasPremium:        hasPremiumFilter,
      carrier:           selectedCarrier !== "any" ? selectedCarrier : null,
      areaCodes:         areaCodesInput.trim() || null,
      createdAt:         now.toISOString(),
      expiresAt:         new Date(
        now.getTime() + rentalMinutes * 60 * 1000
      ).toISOString(),
      graceExpiry:       null,
      timeLeft:          { minutes: rentalMinutes, seconds: 0, expired: false },
      cancelLockLeft:    activeApi === "standard" ? CANCEL_LOCK_SECONDS_STANDARD : CANCEL_LOCK_SECONDS_ALTERNATIVE,
      canCancel:         false,
      isVisible:         true,
    };

    setActiveNumbers((prev) => [optimisticEntry, ...prev]);
    setBuying(false);

    try {
      let res;

      if (activeApi === "standard") {
        res = await api.post("/usa/rent", { service: selectedService.service });
      } else {
        const payload = { service: selectedService.service };
        if (selectedCarrier !== "any") payload.carrier   = selectedCarrier;
        if (areaCodesInput.trim())     payload.areaCodes = areaCodesInput.trim();
        res = await api.post("/tv/rent", payload);
      }

      if (res.data?.success) {
        const realActivation = res.data.activation;
        const realPhone =
          res.data.phone ||
          realActivation?.phone ||
          realActivation?.phoneNumber;

        if (realActivation) {
          const realEntry = processNumber({
            ...realActivation,
            phone:      realPhone,
            chargedNgn: res.data.chargedNgn || realActivation.chargedNgn,
          });

          lastPurchaseRef.current = Date.now();

          setActiveNumbers((prev) =>
            prev.map((n) =>
              n.activationId === optimisticId
                ? { ...realEntry, isVisible: true }
                : n
            )
          );

          setTimeout(() => {
            lastPurchaseRef.current = 0;
            refreshActivations();
          }, POST_PURCHASE_GUARD_MS + 1000);
        }
      } else {
        throw new Error(res.data?.message || "Rental failed");
      }
    } catch (err) {
      setActiveNumbers((prev) =>
        prev.filter((n) => n.activationId !== optimisticId)
      );

      const errData = err.response?.data;
      const code    = errData?.code || "";
      const message = errData?.message || err.message || "Rental failed";

      if (activeApi === "alternative") {
        if (
          ["OUT_OF_STOCK_CARRIER", "OUT_OF_STOCK_AREACODE", "OUT_OF_STOCK_CARRIER_AREACODE"].includes(code)
        ) {
          setFilterError({ message, hint: errData?.hint || "none" });
          toast.warning("Adjust your filter and try again");
        } else if (code === "OUT_OF_STOCK") {
          setShowSwitchHint(true);
          toast.warning(
            `${selectedService.name} not available on Alternative. Try Standard.`
          );
        } else {
          toast.error(message, { autoClose: 6000 });
        }
      } else {
        // Standard (Daisy) — no carrier/area-code filters, and Daisy should
        // always have stock, so we don't prompt switching to Alternative.
        if (code === "OUT_OF_STOCK") {
          toast.error(
            `${selectedService.name} is temporarily out of stock. Please try again in a moment.`,
            { autoClose: 6000 }
          );
        } else {
          toast.error(message, { autoClose: 6000 });
        }
      }
    }
  };

  // ── CANCEL ─────────────────────────────────────────────────────────────────
  const handleCancel = async (activationId) => {
    const entry = activeNumbers.find((n) => n.activationId === activationId);
    if (entry?._optimistic) {
      setActiveNumbers((prev) =>
        prev.filter((n) => n.activationId !== activationId)
      );
      return;
    }

    const isAlt   = entry?.provider === "getatext";
    const toastId = toast.loading("Cancelling...");
    try {
      const res = isAlt
        ? await api.post(`/tv/cancel/${activationId}`, {})
        : await api.post(`/usa/cancel/${activationId}`, {});

      if (res.data?.success) {
        setActiveNumbers((prev) =>
          prev.filter((n) => n.activationId !== activationId)
        );
        toast.update(toastId, {
          render: `Cancelled — ₦${(
            res.data.refundAmount || 0
          ).toLocaleString()} refunded`,
          type:      "success",
          isLoading: false,
          autoClose: 5000,
        });
        refreshActivations();
      } else {
        throw new Error(res.data?.message || "Cancel failed");
      }
    } catch (err) {
      toast.update(toastId, {
        render:    err.response?.data?.message || "Cancel failed",
        type:      "error",
        isLoading: false,
        autoClose: 6000,
      });
    }
  };

  const filteredServices = currentCatalog.filter(
    (s) =>
      s.name.toLowerCase().includes(serviceSearch.toLowerCase()) ||
      s.service.toLowerCase().includes(serviceSearch.toLowerCase())
  );

  // ── Countries already present in the loaded catalog (derived from the
  // service name, e.g. "Whatsapp Canada" → "Canada"). USA is always
  // available since it's the default/base catalog with no suffix.
  const availableCountries = new Set(["USA"]);
  currentCatalog.forEach((s) => {
    const match = ALL_COUNTRIES.find((c) =>
      s.name.toLowerCase().includes(c.toLowerCase())
    );
    if (match) availableCountries.add(match);
  });

  // ── Countries not yet in the catalog — shown as disabled "Coming soon"
  // rows in the dropdown. Purely additive: never affects real services.
  const comingSoonCountries = ALL_COUNTRIES.filter(
    (c) =>
      !availableCountries.has(c) &&
      c.toLowerCase().includes(serviceSearch.toLowerCase())
  );

  const stockBadge = (stock) => {
    if (stock === undefined || stock === null) return null;
    if (stock === 0)  return { text: "Out of stock", color: "text-red-500" };
    if (stock <= 10)  return { text: `${stock} left`,      color: "text-amber-500" };
    if (stock <= 50)  return { text: `${stock} available`, color: "text-amber-400" };
    return              { text: `${stock} available`, color: "text-green-500" };
  };

  const selectedCarrierObj  = carriers.find((c) => c.value === selectedCarrier);
  const isOutOfStockService =
    selectedService && activeApi === "alternative" && selectedService.stock === 0;
  const getButtonDisabled   = buying || isOutOfStockService;
  const otherApi            = activeApi === "standard" ? "alternative" : "standard";

  // ── RENDER ──────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">

        {/* ── Editorial hero ── */}
        <div className="relative pt-1 pb-5 mb-5 border-b border-gray-100 dark:border-gray-800 text-center">
          <div className="hidden sm:flex sm:absolute sm:top-0 sm:right-0">
            <button
              onClick={() => {
                setShowHistory((v) => !v);
                if (!showHistory) fetchHistory();
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm font-medium text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 bg-white dark:bg-gray-900 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition"
            >
              <History size={13} />
              History
              {showHistory ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          </div>

          <p className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.14em] text-purple-600 dark:text-purple-400 mb-2">
            Buy Numbers
          </p>
          <h1 className="font-serif text-xl sm:text-3xl font-normal text-gray-900 dark:text-white leading-snug mb-2 px-2 sm:px-0">
            Real US numbers, ready in seconds
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto mb-3">
            Non-VoIP · Credits only charged when you receive a code.
          </p>
          <div className="flex justify-center gap-1.5 sm:gap-2 flex-wrap">
            <span className="flex items-center gap-1 text-[9px] sm:text-[11px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full">
              <ShieldCheck size={10} className="text-green-500 flex-shrink-0" /> Non-VoIP
            </span>
            <span className="flex items-center gap-1 text-[9px] sm:text-[11px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full">
              <Zap size={10} className="text-amber-500 flex-shrink-0" /> Instant delivery
            </span>
            <span className="flex items-center gap-1 text-[9px] sm:text-[11px] text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-800 px-2 sm:px-3 py-1 sm:py-1.5 rounded-full">
              <RefreshCw size={10} className="text-purple-500 flex-shrink-0" /> Refund safe
            </span>
          </div>

          <div className="flex sm:hidden justify-center mt-4">
            <button
              onClick={() => {
                setShowHistory((v) => !v);
                if (!showHistory) fetchHistory();
              }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 bg-white dark:bg-gray-900 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 transition"
            >
              <History size={13} />
              History
              {showHistory ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
            </button>
          </div>
        </div>

        {/* ── History Panel ── */}
        {showHistory && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-5 shadow-sm">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
              <History size={15} className="text-purple-500" /> OTP History
            </h2>
            {loadingHistory ? (
              <div className="text-center py-6">
                <RefreshCw size={18} className="animate-spin mx-auto text-purple-500" />
              </div>
            ) : history.length === 0 ? (
              <div className="text-center py-6">
                <ShieldCheck size={28} className="mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                <p className="text-sm text-gray-400 dark:text-gray-500">No OTPs received yet</p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  Numbers that receive a code will appear here
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {history.map((h) => (
                  <HistoryRow key={h.activationId} h={h} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── API Switcher — three tabs: Standard / Alternative / All countries ── */}
        <div className="mb-4 grid grid-cols-3 gap-1.5 sm:gap-2">
          <button
            onClick={() => handleSwitchApi("standard")}
            className={`text-left rounded-xl p-2.5 sm:p-4 transition-all duration-200 ${
              activeApi === "standard"
                ? "bg-purple-600 text-white shadow-sm"
                : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
            }`}
          >
            <p className="text-[11px] sm:text-sm font-semibold mb-0.5 truncate">Standard</p>
            <p className={`text-[9px] sm:text-xs ${activeApi === "standard" ? "text-purple-100" : "text-gray-500 dark:text-gray-400"}`}>
              US only
            </p>
          </button>
          <button
            onClick={() => handleSwitchApi("alternative")}
            className={`text-left rounded-xl p-2.5 sm:p-4 transition-all duration-200 ${
              activeApi === "alternative"
                ? "bg-purple-600 text-white shadow-sm"
                : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
            }`}
          >
            <p className="text-[11px] sm:text-sm font-semibold mb-0.5 truncate">Alternative</p>
            <p className={`text-[9px] sm:text-xs ${activeApi === "alternative" ? "text-purple-100" : "text-gray-500 dark:text-gray-400"}`}>
              US only
            </p>
          </button>
          <button
            onClick={() => handleSwitchApi("allcountries")}
            className={`text-left rounded-xl p-2.5 sm:p-4 transition-all duration-200 ${
              activeApi === "allcountries"
                ? "bg-purple-600 text-white shadow-sm"
                : "bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200"
            }`}
          >
            <p className="text-[11px] sm:text-sm font-semibold mb-0.5 truncate">All countries</p>
            <p className={`text-[9px] sm:text-xs ${activeApi === "allcountries" ? "text-purple-100" : "text-amber-600 dark:text-amber-400"}`}>
              Coming soon
            </p>
          </button>
        </div>

        {/* ── Buy Card — key forces remount + animation on tab switch ── */}
        <div
          key={animKey}
          className="api-switch-animate bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-5 mb-6"
        >
          {activeApi === "allcountries" ? (
            <AllCountriesPanel countries={ALL_COUNTRIES} />
          ) : (
            <>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Select Service
          </label>

          <div ref={dropdownRef} className="relative">
            <div className="relative">
              <Search
                size={15}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
              />
              <input
                type="text"
                value={serviceSearch}
                onChange={(e) => {
                  setServiceSearch(e.target.value);
                  setShowDropdown(true);
                  if (!e.target.value) handleClearService();
                }}
                onFocus={() => setShowDropdown(true)}
                placeholder="Search: WhatsApp, Telegram, Google..."
                className="w-full pl-9 pr-10 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-700 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 outline-none transition"
              />
              {serviceSearch && (
                <button
                  onClick={handleClearService}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>

            {showDropdown && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-xl max-h-60 overflow-auto z-20">
                {loadingCatalog ? (
                  <div className="p-4 text-sm text-gray-500 text-center">
                    <RefreshCw size={14} className="inline animate-spin mr-2" />
                    Loading...
                  </div>
                ) : filteredServices.length === 0 && comingSoonCountries.length === 0 ? (
                  <div className="p-4 text-sm text-gray-400 text-center">
                    No services found
                  </div>
                ) : (
                  <>
                    {filteredServices.map((s) => {
                      // Standard: Daisy doesn't give us a real stock count, so
                      // we just show a plain "Available" pill as a design cue
                      // (no number). Alternative still shows the real count.
                      const badge =
                        activeApi === "alternative"
                          ? stockBadge(s.stock)
                          : s.stock === 0
                          ? { text: "Out of stock", color: "text-red-500" }
                          : { text: "Available", color: "text-green-500" };
                      return (
                        <div
                          key={s.service}
                          onClick={() => handleSelectService(s)}
                          className={`px-4 py-2.5 text-sm cursor-pointer hover:bg-purple-50 dark:hover:bg-purple-900/20 transition flex items-center justify-between gap-2 ${
                            selectedService?.service === s.service
                              ? "bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300"
                              : "text-gray-900 dark:text-gray-100"
                          }`}
                        >
                          <span className="flex-1 truncate">{s.name}</span>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {badge && (
                              <span className={`text-xs ${badge.color}`}>
                                ({badge.text})
                              </span>
                            )}
                            <span className="text-purple-600 dark:text-purple-400 font-semibold text-xs">
                              <Currency amount={s.priceNgn} />
                            </span>
                          </div>
                        </div>
                      );
                    })}

                    {comingSoonCountries.length > 0 && (
                      <>
                        {filteredServices.length > 0 && (
                          <div className="px-4 py-1.5 text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-600 border-t border-gray-100 dark:border-gray-800">
                            More countries
                          </div>
                        )}
                        {comingSoonCountries.map((c) => (
                          <div
                            key={c}
                            className="px-4 py-2.5 text-sm flex items-center justify-between gap-2 text-gray-400 dark:text-gray-600 cursor-not-allowed select-none"
                          >
                            <span className="flex-1 truncate">{c}</span>
                            <span className="text-[10px] font-medium px-2 py-0.5 rounded-full border border-gray-200 dark:border-gray-700 text-gray-400 dark:text-gray-500 flex-shrink-0">
                              Coming soon
                            </span>
                          </div>
                        ))}
                      </>
                    )}
                  </>
                )}
              </div>
            )}
          </div>

          {/* ── Popular service quick-chips — real catalog data only ── */}
          {!selectedService && popularServices.length > 0 && (
            <div className="mt-3">
              <p className="text-[10px] sm:text-xs text-gray-400 dark:text-gray-500 mb-1.5">Popular</p>
              <div className="flex gap-1.5 sm:gap-2 overflow-x-auto pb-1">
                {popularServices.map((p) => (
                  <button
                    key={p.key}
                    onClick={() => handleSelectService(p.svc)}
                    className="flex-shrink-0 flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 text-[11px] sm:text-xs font-medium text-gray-700 dark:text-gray-200 hover:border-purple-300 dark:hover:border-purple-700 transition"
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: p.color }}
                    />
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── Switch hint when out of stock ── */}
          {showSwitchHint && selectedService && (
            <div className="mt-3 flex items-start gap-2.5 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
              <AlertCircle
                size={15}
                className="text-amber-500 flex-shrink-0 mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-amber-800 dark:text-amber-300 font-medium">
                  <span className="font-bold">{selectedService.name}</span> is
                  not available on{" "}
                  <span className="capitalize font-bold">{activeApi}</span>.
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                  Switch to{" "}
                  <button
                    onClick={() => handleSwitchApi(otherApi)}
                    className="underline font-semibold capitalize hover:text-amber-900 dark:hover:text-amber-200 transition"
                  >
                    {otherApi}
                  </button>{" "}
                  to check availability there.
                </p>
              </div>
              <button
                onClick={() => setShowSwitchHint(false)}
                className="text-amber-400 hover:text-amber-600 flex-shrink-0"
              >
                <X size={13} />
              </button>
            </div>
          )}

          {/* ── Standard: point people to Alternative for area code/carrier ── */}
          {activeApi === "standard" && (
            <button
              type="button"
              onClick={() => handleSwitchApi("alternative")}
              className="mt-3 flex items-center gap-2 px-3 py-2 w-full rounded-xl border border-dashed border-teal-300 dark:border-teal-700 bg-teal-50/50 dark:bg-teal-900/10 text-sm text-teal-700 dark:text-teal-400 hover:bg-teal-100/60 dark:hover:bg-teal-900/20 transition text-left"
            >
              <MapPin size={14} className="flex-shrink-0" />
              <span className="text-xs sm:text-sm">
                Need a specific area code or carrier?{" "}
                <span className="font-semibold underline">Switch to Alternative</span>
              </span>
            </button>
          )}

          {/* ── Alternative: carrier / area code filters — visible immediately, before picking a service ── */}
          {activeApi === "alternative" && (
            <div className="mt-3">
              {!showFilters ? (
                <button
                  type="button"
                  onClick={() => setShowFilters(true)}
                  className="flex items-center gap-2 px-3 py-2 w-full rounded-xl border border-dashed border-purple-300 dark:border-purple-700 bg-purple-50/50 dark:bg-purple-900/10 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-100/60 dark:hover:bg-purple-900/20 transition group"
                >
                  <span className="flex items-center justify-center w-4 h-4 rounded-full border border-purple-400 dark:border-purple-500 text-purple-500 group-hover:bg-purple-200 dark:group-hover:bg-purple-800 transition text-xs font-bold flex-shrink-0">
                    +
                  </span>
                  <span className="font-medium text-xs sm:text-sm">
                    Filter by carrier or area code
                  </span>
                  <span className="ml-auto text-xs text-purple-400 dark:text-purple-500 bg-purple-100 dark:bg-purple-900/40 px-1.5 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap">
                    +35%
                  </span>
                </button>
              ) : (
                <div className="rounded-xl border border-dashed border-gray-300 dark:border-gray-600 p-2.5 sm:p-4 bg-gray-50 dark:bg-gray-900/50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <AlertCircle
                        size={12}
                        className="text-amber-500 flex-shrink-0"
                      />
                      <p className="text-xs text-gray-500 dark:text-gray-400 leading-tight">
                        <span className="font-semibold text-amber-600 dark:text-amber-400">
                          +35% fee
                        </span>
                        <span className="hidden xs:inline text-gray-400 dark:text-gray-500">
                          {" "}· leave blank for base price
                        </span>
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setShowFilters(false);
                        setSelectedCarrier("any");
                        setAreaCodesInput("");
                        setFilterError(null);
                      }}
                      className="flex-shrink-0 ml-2 p-1 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                    >
                      <X size={13} />
                    </button>
                  </div>

                  <div className="grid grid-cols-2 gap-2">
                    {/* Carrier */}
                    <div>
                      <label className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        <Wifi size={10} />
                        Carrier
                        {loadingCarriers && (
                          <RefreshCw
                            size={9}
                            className="animate-spin ml-0.5 text-purple-500"
                          />
                        )}
                      </label>
                      {loadingCarriers ? (
                        <div className="w-full px-2 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-xs text-gray-400 italic">
                          Checking...
                        </div>
                      ) : (
                        <select
                          value={selectedCarrier}
                          onChange={(e) =>
                            setSelectedCarrier(e.target.value)
                          }
                          className="w-full px-2 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-xs text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 outline-none transition"
                        >
                          {carriers.length > 0 ? (
                            carriers.map((c) => (
                              <option
                                key={c.value}
                                value={c.value}
                                disabled={
                                  c.value !== "any" &&
                                  c.available === false
                                }
                              >
                                {c.label}
                              </option>
                            ))
                          ) : (
                            <>
                              <option value="any">Any</option>
                              <option value="at&t">AT&T</option>
                              <option value="tmobile">T-Mobile</option>
                            </>
                          )}
                        </select>
                      )}
                      {!loadingCarriers &&
                        selectedCarrierObj &&
                        selectedCarrierObj.value !== "any" && (
                          <p
                            className={`text-xs mt-0.5 truncate ${
                              selectedCarrierObj.available === false
                                ? "text-red-500"
                                : selectedCarrierObj.stock <= 10
                                ? "text-amber-500"
                                : "text-green-500"
                            }`}
                          >
                            {selectedCarrierObj.stock !== null &&
                            selectedCarrierObj.stock !== undefined
                              ? selectedCarrierObj.available === false
                                ? "None available"
                                : `${selectedCarrierObj.stock} avail.`
                              : "Stock unknown"}
                          </p>
                        )}
                    </div>

                    {/* Area codes */}
                    <div>
                      <label className="flex items-center gap-1 text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                        <MapPin size={10} />
                        Area code(s)
                      </label>
                      <input
                        type="text"
                        value={areaCodesInput}
                        onChange={(e) =>
                          setAreaCodesInput(e.target.value)
                        }
                        placeholder="212, 646…"
                        className="w-full px-2 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-xs text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 outline-none transition placeholder-gray-400"
                      />
                      <p className="text-xs text-gray-400 mt-0.5">
                        Comma-separated
                      </p>
                    </div>
                  </div>

                  {filterError && (
                    <div className="mt-2 flex items-start gap-1.5 p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                      <AlertTriangle
                        size={12}
                        className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5"
                      />
                      <div>
                        <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
                          {filterError.message}
                        </p>
                        {filterError.hint === "carrier" && (
                          <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                            Try T-Mobile, AT&T, or Any carrier.
                          </p>
                        )}
                        {filterError.hint === "areacode" && (
                          <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                            Try a different area code or clear it.
                          </p>
                        )}
                        {filterError.hint === "carrier_and_areacode" && (
                          <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5">
                            Remove the area code or switch carrier.
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Service selected panel ── */}
          {selectedService && (
            <div className="mt-5 space-y-4">

              {/* Out of stock warning (Alternative only — Standard always has stock) */}
              {isOutOfStockService && (
                <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
                  <AlertTriangle size={15} className="text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-700 dark:text-red-400">
                    No numbers available for {selectedService.name} on Alternative right now.
                    Try Standard instead.
                  </p>
                </div>
              )}

              {!isOutOfStockService && (
                <>

                  {/* ── Mobile price + buy ── */}
                  <div className="md:hidden w-full bg-[#EAF4F2] dark:bg-green-900/20 rounded-xl px-4 py-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {hasPremiumFilter ? "Premium price" : "Price"}
                      </p>
                      <p className="text-2xl font-bold text-gray-900 dark:text-white">
                        <Currency amount={displayPrice} />
                      </p>
                      {hasPremiumFilter && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <span className="text-xs text-gray-400 line-through">
                            <Currency amount={selectedService.priceNgn} />
                          </span>
                          <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">
                            +<Currency amount={premiumExtra} />
                          </span>
                        </div>
                      )}
                    </div>
                    <button
                      onClick={handleGetNumber}
                      disabled={getButtonDisabled}
                      className={`flex-shrink-0 px-5 py-2.5 rounded-xl font-semibold text-white text-sm transition ${
                        getButtonDisabled
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-[#18A999] hover:bg-[#159488] active:bg-[#117a6f]"
                      }`}
                    >
                      Get Number
                    </button>
                  </div>

                  {/* ── Desktop price + buy ── */}
                  <div className="hidden md:flex items-center gap-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl px-5 py-4">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
                        {hasPremiumFilter ? "Premium price" : "Price"}
                      </p>
                      <p className="text-3xl font-bold text-green-700 dark:text-green-300">
                        <Currency amount={displayPrice} />
                      </p>
                      {hasPremiumFilter && (
                        <div className="flex items-center gap-2 mt-1">
                          <span className="text-sm text-gray-400 line-through">
                            <Currency amount={selectedService.priceNgn} />
                          </span>
                          <span className="text-sm text-amber-600 dark:text-amber-400 font-semibold">
                            +<Currency amount={premiumExtra} /> (+35%)
                          </span>
                        </div>
                      )}
                    </div>
                    {hasPremiumFilter && (
                      <div className="flex-1 flex flex-wrap gap-2">
                        {selectedCarrier !== "any" && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-full text-xs font-medium">
                            <Wifi size={11} />
                            {carriers.find((c) => c.value === selectedCarrier)
                              ?.label || selectedCarrier}
                          </span>
                        )}
                        {areaCodesInput.trim() && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full text-xs font-medium">
                            <MapPin size={11} /> Area: {areaCodesInput.trim()}
                          </span>
                        )}
                      </div>
                    )}
                    <button
                      onClick={handleGetNumber}
                      disabled={getButtonDisabled}
                      className={`min-w-[160px] px-6 py-3 rounded-xl font-semibold text-white text-base transition ${
                        getButtonDisabled
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-green-600 hover:bg-green-700 active:bg-green-800 shadow-sm"
                      }`}
                    >
                      Get Number
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
            </>
          )}
        </div>

        {/* ── Active Numbers ── */}
        {(activeNumbers.length > 0 || loadingActive) && (
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 shadow-sm mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
                Active Numbers
                {activeNumbers.length > 0 && (
                  <span className="text-xs font-semibold text-purple-600 dark:text-purple-300">
                    {activeNumbers.length} active
                  </span>
                )}
              </h2>
              <button
                onClick={() => {
                  setLoadingActive(true);
                  refreshActivations();
                }}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition"
              >
                <RefreshCw
                  size={15}
                  className={`text-gray-500 ${loadingActive ? "animate-spin" : ""}`}
                />
              </button>
            </div>

            {loadingActive && activeNumbers.length === 0 ? (
              <div className="text-center py-10">
                <RefreshCw className="w-8 h-8 mx-auto text-purple-600 animate-spin mb-3" />
                <p className="text-sm text-gray-500">Loading...</p>
              </div>
            ) : (
              <div className="space-y-3">
                {activeNumbers.map((n) => (
                  <NumberCard
                    key={n.activationId}
                    n={n}
                    onCancel={() => setCancelModal(n.activationId)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── How it works — numbered steps instead of a bullet dump ── */}
        <div className="mt-2 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl p-4">
          <h3 className="font-serif text-sm sm:text-base font-normal mb-3 text-purple-800 dark:text-purple-200">
            How it works
          </h3>
          <div className="space-y-2 sm:grid sm:grid-cols-2 sm:gap-3 sm:space-y-0">
            {[
              "Select a service and click Get Number — number appears instantly",
              "Credits are only charged if you receive a verification code",
              "Cancel after 1 minute (Standard) if no code arrives — full refund",
              "Alternative numbers can be cancelled anytime before a code arrives",
              "If no OTP before expiry — automatic full refund",
              "After OTP received — 10 extra minutes grace period",
              "Carrier or area code filter (Standard only) adds +35% to the price",
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-2">
                <span className="flex-shrink-0 w-[18px] h-[18px] rounded-full bg-purple-600 text-white text-[9px] font-semibold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <p className="text-[11px] sm:text-xs text-gray-600 dark:text-gray-300 leading-relaxed">
                  {step}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Cancel Modal ── */}
      {cancelModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-5 max-w-sm w-full text-center shadow-2xl">
            <h2 className="text-lg font-bold text-red-600 dark:text-red-400 mb-2">
              Cancel this number?
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-5">
              You will receive a full refund since no OTP has been received yet.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setCancelModal(null)}
                className="flex-1 py-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm font-medium transition"
              >
                Keep It
              </button>
              <button
                onClick={() => {
                  handleCancel(cancelModal);
                  setCancelModal(null);
                }}
                className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition"
              >
                Yes, Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── History Row ───────────────────────────────────────────────────────────────
function HistoryRow({ h }) {
  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };
  return (
    <div className="flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-xl text-sm">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-base">🇺🇸</span>
          <span className="font-medium text-gray-900 dark:text-white truncate">
            {h.phone || h.phoneNumber || "—"}
          </span>
          <button
            onClick={() => copy(h.phone || h.phoneNumber || "", "Number")}
            className="text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            <Copy size={12} />
          </button>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {h.service}
          </span>
          {h._source && (
            <span
              className={`text-xs px-1.5 py-0.5 rounded-full font-medium capitalize ${
                h._source === "standard"
                  ? "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
                  : "bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300"
              }`}
            >
              {h._source}
            </span>
          )}
          {h.carrier && (
            <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded-full">
              {h.carrier}
            </span>
          )}
          <span className="text-xs text-gray-400">·</span>
          <span className="text-xs text-gray-400">
            {h.createdAt
              ? new Date(h.createdAt).toLocaleDateString("en-NG", {
                  day:    "numeric",
                  month:  "short",
                  hour:   "2-digit",
                  minute: "2-digit",
                })
              : "—"}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <div className="text-right">
          <p className="text-xs text-green-600 dark:text-green-400 font-medium">OTP</p>
          <p className="font-mono font-bold text-green-700 dark:text-green-300 text-base tracking-widest">
            {h.otp}
          </p>
        </div>
        <button
          onClick={() => copy(h.otp, "OTP")}
          className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/50 transition"
        >
          <Copy size={13} />
        </button>
      </div>
    </div>
  );
}

// ── Number Card ───────────────────────────────────────────────────────────────
function NumberCard({ n, onCancel }) {
  const { minutes, seconds, expired } = n.timeLeft;
  const timeStr = expired
    ? "Expired"
    : `${minutes}:${seconds.toString().padStart(2, "0")}`;

  const timerColor = expired
    ? "text-red-500 dark:text-red-400"
    : minutes <= 2
    ? "text-red-500 dark:text-red-400 animate-pulse"
    : minutes <= 5
    ? "text-amber-500 dark:text-amber-400"
    : "text-purple-600 dark:text-purple-400";

  const isAlt = n.provider === "getatext";

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied!`);
  };

  // ── Optimistic placeholder ──
  if (n._optimistic) {
    return (
      <div
        className={`rounded-xl border p-3 sm:p-4 ${
          isAlt
            ? "border-teal-200 dark:border-teal-800 bg-teal-50/40 dark:bg-teal-900/10"
            : "border-purple-200 dark:border-purple-800 bg-purple-50/40 dark:bg-purple-900/10"
        }`}
      >
        <div className="flex items-center gap-3">
          <span className="text-lg flex-shrink-0">🇺🇸</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1.5">
              <div
                className={`h-4 w-36 rounded animate-pulse ${
                  isAlt
                    ? "bg-teal-200 dark:bg-teal-800/60"
                    : "bg-purple-200 dark:bg-purple-800/60"
                }`}
              />
              <Loader2
                size={13}
                className={`animate-spin flex-shrink-0 ${
                  isAlt ? "text-teal-500" : "text-purple-500"
                }`}
              />
            </div>
            <div className="flex flex-wrap gap-1.5">
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${
                  isAlt
                    ? "bg-teal-100 dark:bg-teal-900/40 text-teal-700 dark:text-teal-300"
                    : "bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300"
                }`}
              >
                {n.service}
              </span>
              <span
                className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  isAlt
                    ? "bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300"
                    : "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300"
                }`}
              >
                {isAlt ? "Alternative" : "Standard"}
              </span>
            </div>
          </div>
          <div
            className={`text-xs whitespace-nowrap flex items-center gap-1 ${
              isAlt
                ? "text-teal-500 dark:text-teal-400"
                : "text-purple-500 dark:text-purple-400"
            }`}
          >
            <Loader2 size={12} className="animate-spin" />
            Connecting...
          </div>
        </div>
        <div
          className={`mt-2.5 flex items-center gap-2 text-xs ${
            isAlt
              ? "text-teal-500 dark:text-teal-400"
              : "text-purple-500 dark:text-purple-400"
          }`}
        >
          <span
            className={`w-1.5 h-1.5 rounded-full animate-pulse flex-shrink-0 ${
              isAlt ? "bg-teal-500" : "bg-purple-500"
            }`}
          />
          Getting your number...
        </div>
        <div className="mt-2 flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-600 dark:text-gray-300">
            <Currency amount={n.chargedNgn || n.priceNgn || 0} />
          </p>
          <button
            onClick={onCancel}
            className="px-3 py-1 text-xs text-gray-400 hover:text-red-500 transition"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── Real confirmed card ──
  return (
    <div
      className={`rounded-xl border p-3 sm:p-4 transition ${
        expired
          ? "opacity-60 bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-700"
          : n.otpReceived
          ? "bg-green-50/50 dark:bg-green-900/10 border-green-200 dark:border-green-800"
          : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:shadow-md"
      }`}
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span className="text-lg flex-shrink-0">🇺🇸</span>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="font-bold text-sm sm:text-base text-gray-900 dark:text-white break-all">
                {n.phone || n.number || "—"}
              </p>
              <button
                onClick={() => copy(n.phone || n.number || "", "Number")}
                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
              >
                <Copy size={13} />
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">
                {n.service}
              </span>
              <span className="text-xs text-gray-300 dark:text-gray-600">·</span>
              <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">
                United States
              </span>
              {n.carrier && (
                <span className="text-xs bg-blue-100 dark:bg-blue-900/40 px-2 py-0.5 rounded-full text-blue-700 dark:text-blue-300 flex items-center gap-1">
                  <Wifi size={10} /> {n.carrier.toUpperCase()}
                </span>
              )}
              {n.areaCodes && (
                <span className="text-xs bg-indigo-100 dark:bg-indigo-900/40 px-2 py-0.5 rounded-full text-indigo-700 dark:text-indigo-300 flex items-center gap-1">
                  <MapPin size={10} /> {n.areaCodes}
                </span>
              )}
              {n.hasPremium && (
                <span className="text-xs bg-amber-100 dark:bg-amber-900/40 px-2 py-0.5 rounded-full text-amber-700 dark:text-amber-300">
                  +35%
                </span>
              )}
            </div>
          </div>
        </div>
        <div
          className={`flex items-center gap-1 text-sm font-bold whitespace-nowrap ${timerColor}`}
        >
          <Clock size={13} />
          {timeStr}
        </div>
      </div>

      {n.otpReceived && n.otp ? (
        <div className="mt-2 p-3 bg-gray-100 dark:bg-gray-900/60 border border-gray-200 dark:border-gray-700 rounded-none flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs text-gray-500 dark:text-gray-500 mb-1">
              Your {n.service} verification code is
            </p>
            <p className="text-lg sm:text-xl font-mono font-semibold text-gray-500 dark:text-gray-400 tracking-widest break-all leading-snug">
              {n.otp}
            </p>
          </div>
          <button
            onClick={() => copy(n.otp, "OTP")}
            className="p-1.5 bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 rounded-none transition flex-shrink-0 mt-0.5"
          >
            <Copy size={13} className="text-gray-600 dark:text-gray-300" />
          </button>
        </div>
      ) : (
        <div className="mt-2 flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-900/40 rounded-none text-xs text-gray-500 dark:text-gray-400">
          <span className="w-1.5 h-1.5 bg-amber-500 rounded-full animate-pulse flex-shrink-0" />
          Waiting for the {n.service} verification code…
        </div>
      )}

      <div className="mt-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
            <Currency amount={n.chargedNgn || n.priceNgn || 0} />
          </p>
          {n.hasPremium && n.baseNgn && n.baseNgn !== n.chargedNgn && (
            <span className="text-xs text-amber-600 dark:text-amber-400">
              (base <Currency amount={n.baseNgn} /> + 35%)
            </span>
          )}
          {n.otpReceived && (
            <>
              <span className="w-1 h-1 rounded-full bg-gray-400 dark:bg-gray-600 flex-shrink-0" />
              <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                Code received ✓
              </span>
            </>
          )}
        </div>
        {n.status === "pending" && !n.otpReceived && !expired && (
          n.cancelLockLeft > 0 ? (
            <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-2.5 py-1.5 rounded-lg font-medium">
              Cancel in {n.cancelLockLeft}s
            </span>
          ) : (
            <button
              onClick={onCancel}
              className="px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs font-medium transition"
            >
              Cancel &amp; Refund
            </button>
          )
        )}
      </div>
    </div>
  );
}

// ── All Countries Panel ─────────────────────────────────────────────────────
function AllCountriesPanel({ countries }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <Globe size={16} className="text-purple-500 flex-shrink-0" />
        <h2 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          All countries
        </h2>
      </div>
      <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-4">
        We're expanding beyond the US. These countries are on the way — check back soon.
      </p>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {countries.map((c) => (
          <div
            key={c}
            className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl border border-dashed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 cursor-not-allowed select-none"
          >
            <span className="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-400 truncate">
              {c}
            </span>
            <span className="flex-shrink-0 text-[9px] sm:text-[10px] font-semibold text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              Coming soon
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}